import React, { useCallback, useEffect, useState } from "react";

import AlarmRecipientsApi, { AlarmRecipient } from "../../services/AlarmRecipientsApi";

/**
 * `C.4` (`oq-ММБ-206`): раздел «Адресаты тревог» админки.
 *
 * Только `root`: у остальных раздела нет в меню, а сервер отвечает `403` — экран
 * об этом честно говорит, а не показывает пустой список.
 *
 * Что на экране: список (адрес, включён/выключен, кто добавил, когда), добавление,
 * выключение/включение, удаление с подтверждением и пробное письмо. Предел сервера
 * `5 пробных писем в час на учётную запись` показывается словами: из `Retry-After`
 * берётся время повтора в минутах.
 */

/** Минуты до повтора из `Retry-After` (сервер отдаёт секунды). */
export function retryMinutesOf(error: any): number | null {
  const header =
    error?.response?.headers?.["retry-after"] ??
    error?.response?.headers?.["Retry-After"];

  if (header === undefined || header === null || header === "") return null;

  const seconds = Number(header);

  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  return Math.max(1, Math.ceil(seconds / 60));
}

/** Текст отказа: `429` — со временем повтора, остальное — сообщение сервера. */
export function errorTextOf(error: any, fallback: string): string {
  const retryMinutes = retryMinutesOf(error);

  if (retryMinutes !== null) {
    return `Не больше 5 пробных писем в час. Повторите через ${retryMinutes} мин.`;
  }

  const serverMessage = error?.response?.data?.message || error?.response?.data?.error;

  return typeof serverMessage === "string" && serverMessage.trim() !== ""
    ? serverMessage
    : fallback;
}

/** Дата и время добавления в местном виде: `ДД.ММ.ГГГГ ЧЧ:ММ`. */
export function addedAtOf(value: string | null): string {
  if (!value) return "—";

  const at = new Date(value);

  if (Number.isNaN(at.getTime())) return "—";

  return at.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AlarmRecipientsPage() {
  const [rows, setRows] = useState<AlarmRecipient[]>([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await AlarmRecipientsApi.list();
      setRows(list);
      setError(null);
    } catch (requestError) {
      setError(
        errorTextOf(requestError, "Не удалось загрузить адресатов тревог."),
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);

    try {
      await action();
    } catch (requestError) {
      setError(errorTextOf(requestError, "Не удалось выполнить действие."));
    } finally {
      setBusy(false);
    }
  };

  const addRecipient = () =>
    run(async () => {
      const value = email.trim();

      if (value === "") {
        setError("Нужен адрес почты.");
        return;
      }

      await AlarmRecipientsApi.add(value);
      setEmail("");
      setNotice(`Адрес ${value} добавлен.`);
      await load();
    });

  const toggleRecipient = (row: AlarmRecipient) =>
    run(async () => {
      await AlarmRecipientsApi.setEnabled(row.id, !row.enabled);
      await load();
    });

  const removeRecipient = (row: AlarmRecipient) =>
    run(async () => {
      await AlarmRecipientsApi.remove(row.id);
      setConfirmId(null);
      setNotice(`Адрес ${row.email} удалён.`);
      await load();
    });

  const sendTest = (row: AlarmRecipient) =>
    run(async () => {
      await AlarmRecipientsApi.sendTest(row.id);
      setNotice(`Пробное письмо отправлено на ${row.email}.`);
    });

  return (
    <section aria-labelledby="alarm-recipients-title">
      <h2 id="alarm-recipients-title">Адресаты тревог</h2>
      <p>
        На эти адреса уходят письма тревог о связи. Список в окружении
        (`MAIL_ALERT_RECIPIENTS`) — запасной: он работает, пока таблица пуста.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          addRecipient();
        }}
      >
        <label>
          <span>Адрес почты</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-label="Адрес почты"
          />
        </label>
        <button type="submit" disabled={busy}>
          Добавить
        </button>
      </form>

      {error ? (
        <p role="alert" data-testid="alarm-recipients-error">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p role="status" data-testid="alarm-recipients-notice">
          {notice}
        </p>
      ) : null}

      <table>
        <thead>
          <tr>
            <th>Адрес</th>
            <th>Состояние</th>
            <th>Кто добавил</th>
            <th>Когда</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.email}</td>
              <td>{row.enabled ? "включён" : "выключен"}</td>
              <td>{row.created_by === null ? "—" : `№ ${row.created_by}`}</td>
              <td>{addedAtOf(row.created_at)}</td>
              <td>
                <button type="button" disabled={busy} onClick={() => toggleRecipient(row)}>
                  {row.enabled ? "Выключить" : "Включить"}
                </button>
                <button type="button" disabled={busy} onClick={() => sendTest(row)}>
                  Пробное письмо
                </button>
                {confirmId === row.id ? (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => removeRecipient(row)}
                    >
                      Подтвердить удаление
                    </button>
                    <button type="button" onClick={() => setConfirmId(null)}>
                      Отмена
                    </button>
                  </>
                ) : (
                  <button type="button" disabled={busy} onClick={() => setConfirmId(row.id)}>
                    Удалить
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5}>Адресатов нет — тревоги уходят только по списку окружения.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}
